import React, { useState, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactDom from 'react-dom';
import { getAvatarUrlByMemberId } from '../../utilities/url_util';
import { getUserName } from '../../utilities/locale_util';
import MenuLink from '../menu_link';
import { LazyMemberImg } from '../Image';
import { YAHOO_TARGET } from '../../constants/yahoo';
import AreaSwitcher from '../AreaSwitcher';

// styled
import './mobileSideBar.min.css';

const MobileSideBar = ({
  isOpen,
  onClose,
  modalClass,
  member,
  linkData,
  renderRewardPoints,
  renderLotteriesLink,
  onSignOutClick,
  onUserBoxItemClick
}) => {
  const [menuLinkData, setMenuLinkData] = useState([]);
  const [fadeType, setFadeType] = useState(null);
  const backgroundRef = useRef(null);
  const { target } = useSelector(state => state.init);
  const {
    firstName,
    lastName,
    email,
    _id: memberId,
    nickname,
    userid
  } = member;
  const userName = getUserName(firstName, lastName);

  // window.addEventListener
  useEffect(() => {
    const onEscKeyDown = e => {
      if (e.key !== 'Escape') return;
      setFadeType('out');
    };
    window.addEventListener('keydown', onEscKeyDown, false);
    setTimeout(() => setFadeType('in'), 0);
    return () => {
      window.removeEventListener('keydown', onEscKeyDown, false);
    };
  }, []);

  useEffect(() => {
    if (isOpen === false) {
      setFadeType('out');
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (target === YAHOO_TARGET) {
      const data = linkData.filter(item => item.isHiddenFromYahoo === false);

      const yahooMenuData = [
        {
          id: 'YAHOO_FUNLIDAY_INDEX_ID',
          isCurrent: false,
          isExternal: false,
          path: '/',
          textId: 'Homepage',
          isHiddenFromYahoo: false
        },
        ...data
      ];
      setMenuLinkData(yahooMenuData);
    } else {
      setMenuLinkData(linkData);
    }
  }, [linkData]);

  const transitionEnd = e => {
    if (e.propertyName !== 'left' || fadeType === 'in') return;

    if (fadeType === 'out') {
      onClose();
    }
  };

  const handleClick = e => {
    if (e) e.preventDefault();
    setFadeType('out');
  };

  const modalRoot = document.getElementById('modal');
  return ReactDom.createPortal(
    <div
      className={`funliday-siderbar fade-${fadeType} ${modalClass}`}
      role="dialog"
      onTransitionEnd={transitionEnd}
    >
      <div className="box-dialog">
        <div className="user-profile">
          <Link className="user-wrap" to={{ pathname: `/${userid}` }}>
            <div className="user-avatar">
              <LazyMemberImg
                src={getAvatarUrlByMemberId(memberId)}
                alt={nickname || userName}
                title={nickname || userName}
              />
            </div>
            <div className="user-info">
              <span className="user-name">{nickname || userName}</span>
              <span className="user-email">{email}</span>
            </div>
          </Link>
        </div>
        <hr />

        {menuLinkData.map(item => {
          if (item.id === 'MY_NOTIFICATIONS_ID') {
            return (
              <Fragment key={item.id}>
                <div className="user-header-link">
                  <MenuLink
                    id={item.id}
                    key={item.id}
                    isCurrent={item.isCurrent}
                    isExternal={item.isExternal}
                    isSsr={item.isSsr}
                    path={item.path}
                    textId={item.textId}
                    className={item.className}
                    onClick={onUserBoxItemClick}
                  />
                </div>
                <hr />
              </Fragment>
            );
          }
          if (item.id === 'CAR_RENTAL_ID') {
            return (
              <Fragment key={item.id}>
                <div className="user-header-link">
                  <MenuLink
                    id={item.id}
                    key={item.id}
                    isCurrent={item.isCurrent}
                    isExternal={item.isExternal}
                    isSsr={item.isSsr}
                    path={item.path}
                    textId={item.textId}
                    className={item.className}
                    onClick={onUserBoxItemClick}
                  />
                </div>
                <div className="user-header-link">
                  <AreaSwitcher onMenuClick={handleClick} />
                </div>
                <hr />
              </Fragment>
            );
          }
          return (
            <div className="user-header-link" key={item.id}>
              <MenuLink
                id={item.id}
                key={item.id}
                isCurrent={item.isCurrent}
                isExternal={item.isExternal}
                isSsr={item.isSsr}
                path={item.path}
                textId={item.textId}
                className={item.className}
                onClick={onUserBoxItemClick}
              />
            </div>
          );
        })}

        <div className="user-header-link">
          <MenuLink
            id="PROFILE_ID"
            key="PROFILE_ID"
            isCurrent={false}
            isExternal={false}
            path={`/${userid}`}
            textId="Profile"
            onClick={onUserBoxItemClick}
          />
          {renderRewardPoints()}
          <MenuLink
            id="SETTINGS_ID"
            key="SETTINGS_ID"
            isCurrent={false}
            isExternal={false}
            path="/me/settings"
            textId="Settings"
            onClick={onUserBoxItemClick}
          />
          {/* {renderLotteriesLink()} */}
          <div
            role="button"
            tabIndex={0}
            onClick={onSignOutClick}
            onKeyDown={onSignOutClick}
            className="user-sign-out"
          >
            <FormattedMessage id="Sign out" description="Sign out" />
          </div>
        </div>
      </div>
      <div
        className="background"
        onMouseDown={handleClick}
        ref={backgroundRef}
      />
    </div>,
    modalRoot
  );
};

MobileSideBar.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  modalClass: PropTypes.string,
  children: PropTypes.object,
  member: PropTypes.object,
  onSignOutClick: PropTypes.func,
  onUserBoxItemClick: PropTypes.func,
  linkData: PropTypes.array
};
MobileSideBar.defaultProps = {
  modalClass: '',
  onClose: null,
  isOpen: false,
  children: null,
  member: null,
  onSignOutClick: null,
  linkData: [],
  onUserBoxItemClick: null
};

export default MobileSideBar;
